import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { StatusDot } from '@/components/StatusDot';
import { Server, PlusCircle, Trash2, RefreshCw, Loader2, AlertTriangle, Cpu, HardDrive, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/utils';

export default function Workers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get('/workers').then(r => r.data),
    refetchInterval: 20000,
  });

  const refreshMut = useMutation({
    mutationFn: (id) => api.post(`/workers/${id}/refresh`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workers'] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/workers/${id}`).then(r => r.data),
    onSuccess: () => { toast.success('Worker destroyed'); qc.invalidateQueries({ queryKey: ['workers'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Destroy failed'),
  });

  const capacityTable = data?.capacity_table || {};
  const workers = data?.items || [];
  const totalCapacity = workers.filter(w => w.status !== 'deleted').reduce((s, w) => s + (w.agent_capacity || 0), 0);
  const totalConcurrency = workers.filter(w => w.status !== 'deleted').reduce((s, w) => s + (w.concurrent_cap || 0), 0);
  const hourlyCost = workers.filter(w => w.status !== 'deleted').reduce((s, w) => s + ((w.capacity?.cost_hr_eur) || 0), 0);

  return (
    <div className="space-y-6" data-testid="workers-page">
      <PageHeader
        eyebrow="Infrastructure"
        title="Worker fleet"
        description="Hetzner nodes running the Camoufox worker. Each node joins the same Temporal queue — adding a node instantly expands capacity, removing one drains cleanly. Agent state lives in Postgres + R2, so workers are stateless and disposable."
        actions={<ProvisionWorkerDialog capacityTable={capacityTable} />}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={<Server className="h-4 w-4" />} label="Active nodes" value={workers.filter(w => w.status !== 'deleted').length} />
        <StatCard icon={<Cpu className="h-4 w-4" />} label="Agent capacity" value={totalCapacity} hint="total across fleet" />
        <StatCard icon={<Gauge className="h-4 w-4" />} label="Concurrent runs" value={totalConcurrency} hint="simultaneous tasks" />
        <StatCard icon={<HardDrive className="h-4 w-4" />} label="Hourly cost" value={`€${hourlyCost.toFixed(3)}`} hint={`≈ €${(hourlyCost * 24 * 30).toFixed(2)}/mo`} />
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Nodes</div>
        {isLoading ? (
          <div className="p-5 text-xs text-[hsl(var(--text-3))]">Loading…</div>
        ) : workers.length === 0 ? (
          <div className="p-5 text-sm text-[hsl(var(--text-3))]">No worker nodes yet. Provision your first one above.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {workers.map(w => (
              <div key={w.id} className="flex items-center gap-4 p-4" data-testid={`worker-row-${w.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{w.name}</span>
                    {w.is_primary && <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[hsl(var(--primary))]">primary</span>}
                    <StatusChip status={w.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-[hsl(var(--text-3))] font-mono">
                    <span>{w.ipv4 || '—'}</span>
                    <span>{w.server_type} · {w.location}</span>
                    <span>cap {w.agent_capacity} agents · {w.concurrent_cap} concurrent</span>
                    {w.capacity?.cost_hr_eur && <span>€{w.capacity.cost_hr_eur}/h</span>}
                    {w.last_seen_at && <span>seen {relativeTime(w.last_seen_at)}</span>}
                  </div>
                </div>
                <Button variant="ghost" size="icon" title="Refresh from Hetzner" onClick={() => refreshMut.mutate(w.id)} disabled={refreshMut.isPending} data-testid={`refresh-worker-${w.id}`}>
                  <RefreshCw className={`h-4 w-4 ${refreshMut.isPending ? 'animate-spin' : ''}`} />
                </Button>
                <Button variant="ghost" size="icon" title="Destroy node" onClick={() => {
                  if (w.is_primary) { toast.error('Cannot delete primary'); return; }
                  if (confirm(`Destroy ${w.name}? Hetzner will be billed up to this hour. Agents currently running on it will be drained by Temporal.`)) {
                    deleteMut.mutate(w.id);
                  }
                }} disabled={w.is_primary || deleteMut.isPending} data-testid={`delete-worker-${w.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium">Capacity reference (empirical, Camoufox memory-bound)</div>
        <div className="p-5 grid gap-3 md:grid-cols-5 text-xs">
          {Object.entries(capacityTable).map(([ty, c]) => (
            <div key={ty} className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3">
              <div className="text-sm font-medium font-mono">{ty}</div>
              <div className="mt-1 space-y-0.5 text-[hsl(var(--text-2))]">
                <div>{c.ram_gb} GB RAM</div>
                <div>{c.cap_agents} agents / {c.concurrency} concurrent</div>
                <div>€{c.cost_hr_eur}/h · ≈€{(c.cost_hr_eur * 24 * 30).toFixed(2)}/mo</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 p-4 text-xs text-[hsl(var(--text-2))]" data-testid="fleet-notes">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--warning))] mt-0.5" />
          <div className="space-y-1.5">
            <p><strong>After provisioning</strong>: cloud-init installs Docker (~90 s). Then from your local machine run <code className="rounded bg-black/30 px-1 font-mono">bash /app/infra/hetzner/deploy.sh &lt;ipv4&gt;</code> to sync worker code + .env and start the container.</p>
            <p><strong>Billing</strong>: Hetzner bills per hour started. Destroying a node stops the meter immediately.</p>
            <p><strong>Auto-teardown</strong> (planned): a nightly cron will delete any non-primary node idle for {'>'}6 h. For now, destroy manually when you don't need the extra capacity.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, hint }) {
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">{icon}{label}</div>
      <div className="mt-1.5 text-2xl font-semibold tabular">{value}</div>
      {hint && <div className="text-[11px] text-[hsl(var(--text-3))]">{hint}</div>}
    </div>
  );
}

function StatusChip({ status }) {
  const hue = status === 'online' ? 'success' : status === 'provisioning' || status === 'booting' ? 'warning' : status === 'error' || status === 'deleted' ? 'danger' : 'text-3';
  const color = hue === 'success' ? 'hsl(var(--success))' : hue === 'warning' ? 'hsl(var(--warning))' : hue === 'danger' ? 'hsl(var(--danger))' : 'hsl(var(--text-3))';
  return <span className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider border" style={{ borderColor: color, color, background: `${color}22` }} data-testid={`status-chip-${status}`}>{status}</span>;
}

function ProvisionWorkerDialog({ capacityTable }) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', server_type: 'cpx21', location: 'fsn1' });
  const provMut = useMutation({
    mutationFn: (body) => api.post('/workers', body).then(r => r.data),
    onSuccess: (r) => {
      toast.success(`Provisioning ${r.name}`, { description: `IPv4 ${r.ipv4 || '…'} — cloud-init in progress` });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['workers'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Provision failed'),
  });
  const cap = capacityTable[form.server_type] || {};
  const submit = () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    provMut.mutate(form);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="provision-worker-button"><PlusCircle className="mr-2 h-4 w-4" /> Provision worker</Button>
      </DialogTrigger>
      <DialogContent data-testid="provision-dialog">
        <DialogHeader>
          <DialogTitle>Provision a new Hetzner worker</DialogTitle>
          <DialogDescription>
            Creates a Hetzner Cloud VM with Docker preinstalled. Hetzner bills hourly, so you can destroy it anytime when not needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. worker-us-02" data-testid="provision-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Server type</Label>
              <Select value={form.server_type} onValueChange={(v) => setForm({ ...form, server_type: v })}>
                <SelectTrigger data-testid="provision-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(capacityTable).map(([ty, c]) => (
                    <SelectItem key={ty} value={ty}>{ty} — {c.cap_agents} agents · €{c.cost_hr_eur}/h</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fsn1">Falkenstein (DE)</SelectItem>
                  <SelectItem value="nbg1">Nuremberg (DE)</SelectItem>
                  <SelectItem value="hel1">Helsinki (FI)</SelectItem>
                  <SelectItem value="ash">Ashburn (US East)</SelectItem>
                  <SelectItem value="hil">Hillsboro (US West)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {cap.ram_gb && (
            <div className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-3 text-xs text-[hsl(var(--text-2))]">
              <div className="font-mono">{cap.ram_gb} GB RAM · fits ~{cap.cap_agents} agents · {cap.concurrency} concurrent runs · €{(cap.cost_hr_eur * 24 * 30).toFixed(2)}/mo if kept running</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={provMut.isPending} data-testid="provision-submit">
            {provMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Provision (Hetzner bill starts now)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
